import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Ticket, RefreshCw, Plus, Trash2, ShieldCheck } from 'lucide-react'

// Admin: margin-safe discount codes. Backend: /api/v1/admin/coupons (routes/admin.js),
// logic in services/coupons.js. The floor_usd / min_margin_pct fields are the reDNA-style
// margin guard — a discount can never sell a service below its cost floor.

const SERVICES = ['answers', 'sync', 'rag', 'orchestration', 'middleware', 'intake', 'agent']
const emptyDraft = {
  code: '', name: '', discount_type: 'percent', percent: 20, amount_usd: '',
  scope: 'cart', target_service: 'answers', target_ns: '', target_email: '',
  floor_usd: '', min_margin_pct: 20, max_uses: '', max_uses_per_ns: 1, expires_at: '',
}

export default function Coupons() {
  const [rows, setRows] = useState(null)
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(false)
  const [draft, setDraft] = useState(emptyDraft)
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }))

  const load = () => {
    setLoading(true); setErr(null)
    axios.get('/api/v1/admin/coupons')
      .then(r => setRows(r.data?.coupons ?? []))
      .catch(e => setErr(e?.response?.status === 403 ? 'You are not a platform admin.' : 'Could not load coupons.'))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  const add = async () => {
    if (!draft.code.trim()) return
    setSaving(true)
    try {
      const { data } = await axios.post('/api/v1/admin/coupons', {
        ...draft,
        percent: Number(draft.percent) || 0,
        amount_usd: draft.amount_usd === '' ? 0 : Number(draft.amount_usd),
        floor_usd: draft.floor_usd === '' ? 0 : Number(draft.floor_usd),
        min_margin_pct: Number(draft.min_margin_pct) || 0,
        max_uses: draft.max_uses === '' ? null : Number(draft.max_uses),
        max_uses_per_ns: draft.max_uses_per_ns === '' ? 1 : Number(draft.max_uses_per_ns),
        expires_at: draft.expires_at || null,
      })
      setRows(rs => {
        const c = data.coupon; const i = (rs || []).findIndex(x => x.id === c.id)
        return i >= 0 ? rs.map(x => x.id === c.id ? c : x) : [c, ...(rs || [])]
      })
      setDraft(emptyDraft); toast.success('Coupon saved')
    } catch (e) { toast.error(e?.response?.data?.error || 'Save failed') }
    finally { setSaving(false) }
  }

  const toggle = async (c) => {
    try {
      const { data } = await axios.patch(`/api/v1/admin/coupons/${c.id}`, { active: !c.active })
      setRows(rs => rs.map(x => x.id === c.id ? data.coupon : x))
    } catch (e) { toast.error(e?.response?.data?.error || 'Update failed') }
  }
  const remove = async (id) => {
    if (!window.confirm('Delete this coupon?')) return
    try { await axios.delete(`/api/v1/admin/coupons/${id}`); setRows(rs => rs.filter(x => x.id !== id)); toast.success('Deleted') }
    catch (e) { toast.error(e?.response?.data?.error || 'Delete failed') }
  }

  return (
    <div className="max-w-6xl">
      <div className="flex items-center gap-2">
        <Ticket size={20} className="text-primary-600" />
        <h2 className="text-2xl font-bold text-gray-900">Coupons</h2>
        <button onClick={load} className="ml-auto inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>
      <p className="text-gray-500 mt-1">Discount codes for checkout & credit top-ups — with a hard cost floor so a coupon can never sell below cost.</p>

      {/* Create form */}
      <div className="card mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Code</label>
          <input className="input w-full text-sm" placeholder="LAUNCH20" value={draft.code}
            onChange={e => set('code', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Name (internal)</label>
          <input className="input w-full text-sm" placeholder="Launch week" value={draft.name} onChange={e => set('name', e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Discount type</label>
          <select className="input w-full text-sm" value={draft.discount_type} onChange={e => set('discount_type', e.target.value)}>
            <option value="percent">% off</option>
            <option value="fixed">$ off</option>
            <option value="free">Free (comp)</option>
          </select>
        </div>

        {draft.discount_type === 'percent' && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Percent off</label>
            <input className="input w-full text-sm" type="number" min="0" max="100" value={draft.percent} onChange={e => set('percent', e.target.value)} />
          </div>
        )}
        {draft.discount_type === 'fixed' && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Amount off (USD)</label>
            <input className="input w-full text-sm" type="number" min="0" value={draft.amount_usd} onChange={e => set('amount_usd', e.target.value)} />
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Scope</label>
          <select className="input w-full text-sm" value={draft.scope} onChange={e => set('scope', e.target.value)}>
            <option value="cart">Whole cart</option>
            <option value="service">One service</option>
            <option value="namespace">One workspace</option>
            <option value="email">One email</option>
          </select>
        </div>
        {draft.scope === 'service' && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Service</label>
            <select className="input w-full text-sm" value={draft.target_service} onChange={e => set('target_service', e.target.value)}>
              {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        {draft.scope === 'namespace' && (
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Namespace ID</label>
            <input className="input w-full text-sm" value={draft.target_ns} onChange={e => set('target_ns', e.target.value)} /></div>
        )}
        {draft.scope === 'email' && (
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Email</label>
            <input className="input w-full text-sm" value={draft.target_email} onChange={e => set('target_email', e.target.value)} /></div>
        )}

        {/* Margin-safe controls */}
        <div>
          <label className="flex items-center gap-1 text-xs font-semibold text-gray-500 mb-1"><ShieldCheck size={12} className="text-green-600" /> Price floor (USD)</label>
          <input className="input w-full text-sm" type="number" min="0" placeholder="0 = use margin %" value={draft.floor_usd} onChange={e => set('floor_usd', e.target.value)} />
        </div>
        <div>
          <label className="flex items-center gap-1 text-xs font-semibold text-gray-500 mb-1"><ShieldCheck size={12} className="text-green-600" /> Min margin %</label>
          <input className="input w-full text-sm" type="number" min="0" value={draft.min_margin_pct} onChange={e => set('min_margin_pct', e.target.value)} />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Max uses (total)</label>
          <input className="input w-full text-sm" type="number" min="0" placeholder="∞" value={draft.max_uses} onChange={e => set('max_uses', e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Max per workspace</label>
          <input className="input w-full text-sm" type="number" min="1" value={draft.max_uses_per_ns} onChange={e => set('max_uses_per_ns', e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Expires</label>
          <input className="input w-full text-sm" type="date" value={draft.expires_at} onChange={e => set('expires_at', e.target.value)} />
        </div>

        <div className="md:col-span-3">
          <button onClick={add} disabled={!draft.code.trim() || saving} className="btn-primary text-sm flex items-center gap-1 disabled:opacity-50">
            <Plus size={15} /> {saving ? 'Saving…' : 'Save coupon'}
          </button>
        </div>
      </div>

      {err && <div className="card mt-6 text-sm text-red-500">{err}</div>}
      {!err && (
        <div className="card mt-6 p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="px-4 py-3 font-semibold">Code</th>
                <th className="px-4 py-3 font-semibold">Discount</th>
                <th className="px-4 py-3 font-semibold">Scope</th>
                <th className="px-4 py-3 font-semibold">Floor</th>
                <th className="px-4 py-3 font-semibold">Used</th>
                <th className="px-4 py-3 font-semibold">Expires</th>
                <th className="px-4 py-3 font-semibold">Active</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows === null ? <tr><td colSpan={8} className="px-4 py-6 text-gray-500">Loading…</td></tr>
                : rows.length === 0 ? <tr><td colSpan={8} className="px-4 py-6 text-gray-500">No coupons yet.</td></tr>
                : rows.map(c => (
                  <tr key={c.id} className="border-b border-gray-200/60 hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono font-semibold text-gray-900">{c.code}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {c.discount_type === 'free' ? 'Free' : c.discount_type === 'fixed' ? `$${c.amount_usd} off` : `${c.percent}% off`}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{c.scope}{c.target_service ? `: ${c.target_service}` : ''}</td>
                    <td className="px-4 py-3 text-gray-600">{Number(c.floor_usd) > 0 ? `$${c.floor_usd}` : `${c.min_margin_pct}% margin`}</td>
                    <td className="px-4 py-3 text-gray-600">{c.used_count}{c.max_uses != null ? ` / ${c.max_uses}` : ''}</td>
                    <td className="px-4 py-3 text-gray-500">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggle(c)} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${c.active ? 'bg-green-500/10 text-green-600' : 'bg-gray-200 text-gray-500'}`}>
                        {c.active ? 'Active' : 'Paused'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => remove(c.id)} className="p-2 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
